"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
const PROJECT_TYPE_OPTIONS = ["Custom AV Design", "Turnkey System Integration", "Installation Services", "Full-Scope AV Solution"];
function IntegrationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-border/50 hover:bg-accent/50">
          Submit Another Request
        </Button>
      </div>;
  }
  const inputBaseClasses = "flex w-full min-w-0 rounded-md border border-border/30 bg-black/40 px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm placeholder:text-muted-foreground/50";
  return <Form onSubmit={handleSubmit} className="space-y-6 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-black/40 border-border/30 focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        required: "Company is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Company / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp." className="bg-black/40 border-border/30 focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-black/40 border-border/30 focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" className="bg-black/40 border-border/30 focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Best number to reach you</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" rules={{
        required: "Please select a project type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Project Type</FormLabel>
              <FormControl>
                <select className={`${inputBaseClasses} h-9 appearance-none`} {...field}>
                  <option value="" disabled>Select a project type</option>
                  {PROJECT_TYPE_OPTIONS.map((opt, i) => <option key={opt} value={opt} data-index={i} className="bg-background text-foreground">
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Estimated Budget (USD)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 25000" className="bg-black/40 border-border/30 focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Optional – helps us tailor the solution</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground">Project Details / Requirements</FormLabel>
            <FormControl>
              <textarea placeholder="Brief description of your integration needs..." className={`${inputBaseClasses} min-h-[120px] resize-y`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_20px_rgba(225,29,72,0.3)] transition-all duration-300 hover:shadow-[0_0_30px_rgba(225,29,72,0.5)]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Request...
          </> : 'Submit Request'}
      </Button>
    </Form>;
}
export default function StartYourIntegration() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="start-your-integration" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-primary/15 blur-[150px] rounded-full pointer-events-none opacity-70" />
      
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none" />

      <div ref={ref} className={`container mx-auto px-4 md:px-6 relative z-10 max-w-4xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Ready to Transform Your Space?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Start Your Integration. Provide us with a few details about your project, and our team will contact you about starting a tailored AV solution for your environment.</p>
        </div>

        <div className="bg-card/30 backdrop-blur-md border border-border/20 rounded-2xl p-6 md:p-10 shadow-2xl relative overflow-hidden">
          {}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
          
          <FormProvider formId="start_integration_form_home" sectionName="start-your-integration" defaultValues={{
          name: "",
          email: "",
          phone: "",
          company: "",
          project_type: "",
          budget: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true
          },
          company: {
            required: true
          },
          project_type: {
            required: true
          }
        }} submitText="Submit Request" successMessage="Thank you! Your integration request has been successfully submitted. Our team will review your details and contact you shortly.">
            <IntegrationFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}